import { useBrushCollapseModel } from '@/store/collapse-models/brush-collapse-model';
import { useCollapseModel } from '@/store/collapse-models/collapse-model';
import './brush-collapse.less';
import { useLatest } from 'ahooks';
import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Scrollbars, Theme, Title } from '@meitu/saas-ui';
import { CustomTitle } from '@meitu/saas-ui/es/title';
import { BrushType, IBrushDrawConfig, IMarkupDrawConfig, MarkupType } from '@meitu/saas-editor-core/es/version-types';
import { CollapseType } from '@meitu/saas-editor-ui/es/types';
import IconFont from '@meitu/xdesign-icon-font';
import { CHANGE_BRUSH_TAG_EVENT, events } from '@/events-bus';
import { useFeatureHelper } from '@/hooks';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { useEditorModel } from '@/store/models/editor-model';
import { markupFree } from '@/types';
import { isInBrushTest } from '@/utils/ab-test';
import { abBrushList, brushList, BrushTagType, getPreviewImage, statTypeIdMap } from './config';
import CustomStyleBtn from './custom-btn/custom-style-btn';
import TagMenu from './tag-menu/tag-menu';

const { AppTheme } = Theme;
const { BackTitle } = Title;

const BrushStyleList: React.FC<{
    hiddenTitle?: boolean;
    type: BrushTagType;
    customNode?: React.ReactNode;
    maxRenderCount: number;
    activeId: string;
    title: string;
    paddingBottom?: number;
    /** 限免标签 */
    free?: boolean;
    data: { config: { id: string; bg?: string; vip?: string }; data: any }[];
    onChange: (id: string, item: any, type: BrushTagType) => void;
    onShowAll?: (type: BrushTagType) => void;
}> = React.memo(
    ({
        type,
        customNode,
        activeId,
        title,
        data,
        onChange,
        onShowAll,
        paddingBottom = 0,
        maxRenderCount,
        hiddenTitle,
        free,
    }) => {
        const { $t } = useLocaleStore();
        const theme = useEditorModel((state) => state.theme);
        const showAll = hiddenTitle ? true : data.length > maxRenderCount;

        return (
            <>
                {hiddenTitle ? null : (
                    <div className="c-brush-collapse__title">
                        <CustomTitle
                            leftNode={
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {title}
                                    {free ? (
                                        <div className="c-brush-collapse__title-free">
                                            {/* 限免标签 */}
                                            {$t((v) => v.collapse.brushCollapse.free)}
                                        </div>
                                    ) : null}
                                </div>
                            }
                            rightNode={
                                showAll ? (
                                    <div
                                        onClick={() => {
                                            onShowAll?.(type);
                                        }}
                                        className="c-brush-collapse__title-all"
                                    >
                                        {$t((v) => v.collapse.brushCollapse.all)}
                                        <IconFont fontSize={12} name="ChevronRightBlack" />
                                    </div>
                                ) : null
                            }
                        />
                    </div>
                )}
                <div
                    className={classnames('c-brush-collapse__list', {
                        'show-all': hiddenTitle,
                    })}
                    style={{ paddingBottom }}
                >
                    {customNode}
                    {data.map((item, index) => {
                        if (index < maxRenderCount) {
                            return (
                                <div
                                    key={index}
                                    onClick={() => {
                                        onChange(item.config.id, item.data, type);
                                    }}
                                    className={classnames('c-brush-collapse__item', {
                                        active: item.config.id === activeId,
                                    })}
                                >
                                    <div
                                        className="c-brush-collapse__item-wrap"
                                        style={{
                                            ...(theme !== AppTheme.dark &&
                                                item.config.bg && {
                                                    background: item.config.bg,
                                                }),
                                        }}
                                    >
                                        {item.config.vip ? (
                                            <div className="c-brush-collapse__item-wrap-vip">
                                                <img src={item.config.vip} alt="" />
                                            </div>
                                        ) : null}
                                        {getPreviewImage(item.config.id, 244, 120)}
                                    </div>
                                </div>
                            );
                        }
                        return <React.Fragment key={index} />;
                    })}
                </div>
            </>
        );
    },
);

const BrushCollapse = () => {
    const { $t } = useLocaleStore();
    const featureHelper = useFeatureHelper();

    const currentType = useCollapseModel((state) => state.currentType);
    const setActualType = useCollapseModel((state) => state.setActualType);
    const setCurrentType = useCollapseModel((state) => state.setCurrentType);
    const from = useBrushCollapseModel((state) => state.from);
    const setFrom = useBrushCollapseModel((state) => state.setFrom);

    const [currentTag, setCurrentTag] = useState<BrushTagType>(BrushTagType.all);
    const [popoverId, setPopoverId] = useState('');
    const trackTickCode = useRef<number | string>('');
    const isFirstClickCustomBtn = useRef<any>({
        [BrushTagType.line]: true,
        [BrushTagType.curve]: true,
    });

    const [activeId, setActiveId] = useState('');
    const activeIdRef = useLatest(activeId);

    const handleBack = () => {
        if (!from) return;

        exitBrush();

        setFrom(null);
        setCurrentType(from);
        setActualType(null);
    };

    const getSecondId = function (brushType: BrushType, trackCode?: string | number) {
        if (trackCode) {
            trackTickCode.current = trackCode;
            return trackCode;
        }
        let code = 106;
        if (brushType === BrushType.Arrow || brushType === BrushType.Line) {
            code = 104;
        } else if (brushType === BrushType.Curve) {
            code = 105;
        }
        trackTickCode.current = code;
        return code;
    };
    const getMarkupSecondId = function (markupType: MarkupType, trackCode?: string | number) {
        if (trackCode) {
            trackTickCode.current = trackCode;
            return trackCode;
        }
        const code = 160;
        trackTickCode.current = code;
        return code;
    };

    const exitBrush = () => {
        editor.brushDraw.close();
        editor.markupDraw.close();
    };

    // 列表选中
    const onStyleListChangeHandle = useCallback(
        (id: string, item: any, type: BrushTagType, disableToggle?: boolean, trackCode?: string) => {
            if (id === activeId && !disableToggle) {
                exitBrush();
            } else {
                // exitBrush();
                // 设置打开画笔
                if (type === BrushTagType.markup) {
                    // 先关闭画板再打开
                    editor.markupDraw.close();
                    editor.brushDraw.close();
                    setTimeout(() => {
                        setActiveId(id);
                        editor.markupDraw.open(item);
                    }, 0);
                } else {
                    editor.markupDraw.close();
                    setTimeout(() => {
                        setActiveId(id);
                        editor.brushDraw.open(item);
                    }, 0);
                    // 第一次点击自动弹出自定义面板
                    setPopoverId(isFirstClickCustomBtn.current[type] ? type : '');
                    isFirstClickCustomBtn.current[type] = false;
                }
            }

            const value = statTypeIdMap[item.markupType ? 'markup' : (item.brushType as string)];
            if (value) {
                window._stat_track('tpbj_material_click', {
                    type_id: value,
                    material_id: id,
                    func: 'draw',
                });
            }
        },
        [activeId],
    );

    // 自定义弹窗属性变更
    const onAttrChangeHandle = useCallback((item: any, type: BrushTagType) => {
        if (type === BrushTagType.markup) {
            if (markupFree) {
                item.__vipFuncType = '';
            }
            editor.markupDraw.open(item);
        } else {
            editor.brushDraw.open(item);
        }
    }, []);

    // 标签切换
    const onTagSelectHandle = useCallback((type: BrushTagType) => {
        exitBrush();
        setCurrentTag(type);
    }, []);

    useEffect(() => {
        // 注册画笔菜单打开/关闭事件
        featureHelper.register(CollapseType.BRUSH_MENU, {
            leave() {
                console.log('leave brush');
                exitBrush();
            },
            enter() {
                console.log('enter brush');
            },
        });
        // 画笔绘制结束
        editor.brushDraw.onDrawEnd((brushType: BrushType) => {
            setActiveId('');
            const value = statTypeIdMap[brushType];
            if (value) {
                window._stat_track('tpbj_material_tick', {
                    type_id: value,
                    func: 'draw',
                });
            }
        });
        // 画笔绘制配置变化
        editor.brushDraw.onConfigChange((drawConfig: IBrushDrawConfig | null) => {
            if (!drawConfig || drawConfig.__isQuickDraw) {
                setActiveId('');
            }
        });
        // 标注笔绘制结束
        editor.markupDraw.onDrawEnd((markupType: MarkupType) => {
            setActiveId('');
            window._stat_track('tpbj_material_tick', {
                type_id: statTypeIdMap['markup'],
                func: 'draw',
            });
        });
        // 标注笔绘制配置变化
        editor.markupDraw.onConfigChange((drawConfig: IMarkupDrawConfig | null) => {
            if (!drawConfig || drawConfig.__isQuickDraw) {
                setActiveId('');
            }
        });
    }, []);

    /** 关闭自定义弹窗 */
    useEffect(() => {
        if (!activeId || (activeId !== BrushTagType.line && activeId !== BrushTagType.curve)) {
            setPopoverId('');
        }
    }, [activeId]);

    // 列表配置
    const brushListData = useMemo(() => {
        const list = isInBrushTest() ? abBrushList() : brushList();
        return list;
    }, []);

    useEffect(() => {
        // if (currentTag === BrushTagType.markup) return;
        if (currentTag === BrushTagType.all) {
            Object.values(brushListData).forEach((item: any) => {
                item.forEach((child: any) => {
                    const key = child.data.markupType ? 'markup' : (child.data.brushType as string);
                    const value = statTypeIdMap[key];
                    if (value) {
                        window._stat_track('tpbj_material_show', {
                            type_id: value,
                            material_id: child?.config?.id || '',
                            func: 'draw',
                        });
                    }
                });
            });
            return;
        }
        brushListData[currentTag].forEach((item: any) => {
            const key = item.data.markupType ? 'markup' : (item.data.brushType as string);
            const value = statTypeIdMap[key];
            if (value) {
                window._stat_track('tpbj_material_show', {
                    type_id: value,
                    material_id: item?.config?.id || '',
                    func: 'draw',
                });
            }
        });
    }, [brushListData, currentTag]);

    const onPopoverHandle = useCallback((id: string) => {
        setPopoverId(id);
    }, []);

    const isRenderAll = currentTag === BrushTagType.all;

    useEffect(() => {
        // 快捷切换笔刷 tag
        const handler = events.on(CHANGE_BRUSH_TAG_EVENT, (tag: BrushTagType) => {
            setCurrentTag(tag);
        });
        return () => {
            events.off(handler);
        };
    }, []);

    return (
        <div className="c-brush-collapse">
            {from ? (
                <div className="c-element-collapse__title">
                    <BackTitle text={$t((v) => v.collapse.shortcutCollapse.Brush.title)} handleBackClick={handleBack} />
                </div>
            ) : null}
            <div className="c-brush-collapse__top">
                <TagMenu selectedValue={currentTag} onChange={onTagSelectHandle} />
            </div>
            <div className="c-brush-collapse__content" style={{ marginTop: isRenderAll ? 4 : 0 }}>
                <Scrollbars autoHide={true}>
                    <div className="c-brush-collapse__content">
                        {/* 直线笔 */}
                        {isRenderAll || currentTag === BrushTagType.line ? (
                            <BrushStyleList
                                hiddenTitle={currentTag === BrushTagType.line}
                                type={BrushTagType.line}
                                maxRenderCount={!isRenderAll ? brushListData.line.length : 8}
                                customNode={
                                    <CustomStyleBtn
                                        hiddenTitle={currentTag === BrushTagType.line}
                                        activeId={activeId}
                                        popoverId={popoverId}
                                        type={BrushTagType.line}
                                        onPopover={onPopoverHandle}
                                        onClick={onStyleListChangeHandle}
                                        onChange={onAttrChangeHandle}
                                        trackCode="113"
                                    />
                                }
                                activeId={activeId}
                                title={$t((v) => v.collapse.brushCollapse.linePen)}
                                data={brushListData.line}
                                paddingBottom={10}
                                onChange={onStyleListChangeHandle}
                                onShowAll={onTagSelectHandle}
                            />
                        ) : null}
                        {/* 形状 */}
                        {isRenderAll || currentTag === BrushTagType.shape ? (
                            <BrushStyleList
                                hiddenTitle={currentTag === BrushTagType.shape}
                                type={BrushTagType.shape}
                                maxRenderCount={!isRenderAll ? brushListData.shape.length : 9}
                                activeId={activeId}
                                title={$t((v) => v.collapse.brushCollapse.shape)}
                                data={brushListData.shape}
                                paddingBottom={10}
                                onChange={onStyleListChangeHandle}
                                onShowAll={onTagSelectHandle}
                            />
                        ) : null}
                        {/* 标注笔 */}
                        {isRenderAll || currentTag === BrushTagType.markup ? (
                            <BrushStyleList
                                hiddenTitle={currentTag === BrushTagType.markup}
                                type={BrushTagType.markup}
                                maxRenderCount={!isRenderAll ? brushListData.markup.length : 9}
                                activeId={activeId}
                                title={$t((v) => v.collapse.brushCollapse.markupPen)}
                                data={brushListData.markup}
                                paddingBottom={10}
                                onChange={onStyleListChangeHandle}
                                onShowAll={onTagSelectHandle}
                            />
                        ) : null}
                        {/* 曲线笔 */}
                        {isRenderAll || currentTag === BrushTagType.curve ? (
                            <BrushStyleList
                                hiddenTitle={currentTag === BrushTagType.curve}
                                type={BrushTagType.curve}
                                maxRenderCount={!isRenderAll ? brushListData.curve.length : 8}
                                customNode={
                                    <CustomStyleBtn
                                        hiddenTitle={currentTag === BrushTagType.curve}
                                        activeId={activeId}
                                        popoverId={popoverId}
                                        type={BrushTagType.curve}
                                        onPopover={onPopoverHandle}
                                        onClick={onStyleListChangeHandle}
                                        onChange={onAttrChangeHandle}
                                        trackCode="114"
                                    />
                                }
                                activeId={activeId}
                                title={$t((v) => v.collapse.brushCollapse.curvePen)}
                                data={brushListData.curve}
                                onChange={onStyleListChangeHandle}
                                onShowAll={onTagSelectHandle}
                            />
                        ) : null}
                    </div>
                </Scrollbars>
            </div>
        </div>
    );
};
export default React.memo(BrushCollapse);
